import type { AIRequest, AIResponse } from '@/types'

// ─── Secure AI proxy transport ────────────────────────────────────────────────
// All AI calls go to this app's own /api/ai Pages Function. API keys live as
// Cloudflare Pages secrets (server-side only) — never in this bundle.

async function callServer(request: AIRequest): Promise<AIResponse> {
  const res = await fetch('/api/ai', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      prompt: request.prompt,
      systemPrompt: request.systemPrompt,
      maxTokens: request.maxTokens,
    }),
  })
  if (!res.ok) throw new Error(`AI proxy error ${res.status}`)
  const data = await res.json()
  return { content: data.content ?? '', provider: data.provider }
}

export async function callAI(request: AIRequest): Promise<AIResponse> {
  return callServer(request)
}

export async function generateSanctumDNASummary(
  dnaName: string,
  answers: string[],
  destinations: string[]
): Promise<string> {
  const destList = destinations.join(', ')
  const res = await callAI({
    systemPrompt: 'You are Sanctum, a reverent guide for sacred and ancestral journeys. Write with quiet authority and deep respect — two sentences maximum. Address the traveller directly.',
    prompt: `A traveller has come to Sanctum. Their sacred DNA archetype is "${dnaName}". Their answers were: ${answers.join('; ')}. The traveller's matched destinations are: ${destList}. Reference ONLY these places by name if you name any place at all. Never mention any destination not in this list. Write a personalised 2-sentence reflection on what calls them — quiet, profound, never generic.`,
    maxTokens: 150,
  })
  return res.content.trim()
}

// ─── Sanctum Itinerary Generator ──────────────────────────────────────────────
// Mirrors Passage's generatePassageItinerary — reverent tone, JSON array of
// {day,title,highlight}. Always resolves; on any AI/parse failure it silently
// falls back to curated day placeholders so the itinerary screen NEVER blanks.
export async function generateSanctumItinerary(
  destination: string,
  dnaName: string,
  days: number
): Promise<{ day: number; title: string; highlight: string }[]> {
  try {
    const res = await callAI({
      systemPrompt: 'You are Sanctum, a reverent guide for sacred and ancestral pilgrimages. Respond ONLY with valid JSON — no markdown, no extra text.',
      prompt: `Generate a ${days}-day pilgrimage itinerary for a "${dnaName}" traveller visiting ${destination}. Each day should honour the sacred, ancestral or spiritual significance of the place — quiet reverence, never touristic. Return a JSON array of ${days} objects: [{"day":1,"title":"short reverent title (max 5 words)","highlight":"one sentence of the day's sacred purpose (max 25 words)"}]`,
      maxTokens: 600,
    })
    const parsed = JSON.parse(res.content.replace(/```json|```/g, '').trim())
    if (Array.isArray(parsed) && parsed.length > 0) return parsed
    throw new Error('empty itinerary')
  } catch {
    return Array.from({ length: days }, (_, i) => ({
      day: i + 1,
      title: `Day ${i + 1}`,
      highlight: `A day of quiet pilgrimage in ${destination}.`,
    }))
  }
}
